import React from 'react';

import { useGqlContext } from '@search/gql-client/src/gqlContext';
import { graphql } from '@search/gql-client/src/useGql';
import { useGql2Loader } from '@search/gql-client/src/useGql2';
import { MortgagePurpose, MortgagePropertyType, MortgageProgramCodeType } from '@search/graphql-typings';
import { formatPrice } from '@search/helpers/src/formatPrice';
import { Currency } from '@search/filter-enums/enums/Currency';
import useDebouncedCallback from '@search/vtbeco-frontend-core/view/common/hooks/useDebouncedCallback';

import type {
    BanksMortgageOffersQuery$data as BanksMortgageOffersQueryResponse,
    BanksMortgageOffersQuery$variables as BanksMortgageOffersQueryVariables
} from './__generated__/BanksMortgageOffersQuery.graphql';

const BanksMortgageOffersQuery = graphql`
    query BanksMortgageOffersQuery(
        $term: Int!
        $propertyCost: Float!
        $ownAmount: Float!
        $regionId: Int!
        $purpose: MortgagePurpose!
        $propertyType: MortgagePropertyType!
        $programCode: MortgageProgramCodeType!
    ) {
        banksMortgageOffers(
            term: $term
            propertyCost: $propertyCost
            ownAmount: $ownAmount
            regionId: $regionId
            purpose: $purpose
            propertyType: $propertyType
            programCode: $programCode
        ) {
            name
            bankId
            minimalPercent
            minimalPercentFormatted
            minimalPayment
            minimalPaymentFormatted
            icon {
                origin
            }
        }
    }
`;

export function useBanksMortgageOffersFetcher() {
    const gql = useGqlContext();

    return React.useCallback(
        (vars: BanksMortgageOffersQueryVariables, opts?: { signal?: AbortSignal }) => gql.client<
            BanksMortgageOffersQueryVariables,
            BanksMortgageOffersQueryResponse
        >(BanksMortgageOffersQuery, vars, opts)
            .then(response => {
                return response;
            }),
        [ gql ]
    );
}

export function useBanksMortgageOffersLoader() {
    return useGql2Loader(
        useBanksMortgageOffersFetcher(),
        { cachePrefix: 'banksMortgageOffers' }
    );
}

export interface UseBankMortgageFormProps {
    /* Срок займа */
    term: number;

    /* Стоимость недвижимости */
    propertyCost: number;

    /* Сумма собственных средств */
    ownAmount: number;

    /* Тип ипотеки (Вторичка, Новостройка, Рефинансирование) */
    purpose: typeof MortgagePurpose[keyof typeof MortgagePurpose];

    /* id Региона (отличается от id в классифайде) */
    regionId: number;

    /* Тип недвижимости */
    propertyType: typeof MortgagePropertyType[keyof typeof MortgagePropertyType];

    /* Код ипотечной программы */
    programCode: typeof MortgageProgramCodeType[keyof typeof MortgageProgramCodeType];

    onChange: (data: Omit<UseBankMortgageFormProps, 'onChange'>) => void;
}

export type BanksMortgageOffers = BanksMortgageOffersQueryResponse['banksMortgageOffers'];

export const useBanksMortgageOffers = ({
    term,
    propertyCost,
    ownAmount,
    purpose,
    regionId,
    propertyType,
    programCode
}: Omit<UseBankMortgageFormProps, 'onChange'>) => {
    const [ mortgageFormData, setMortgageFormData ] = React.useState({
        term,
        ownAmount: Math.round(ownAmount),
        propertyCost: Math.round(propertyCost),
        purpose,
        regionId,
        propertyType,
        programCode
    });

    const [ setFormData ] = useDebouncedCallback(setMortgageFormData, 500);

    const isNeedSendRequest = React.useMemo(() => (
        mortgageFormData.ownAmount > 0 && mortgageFormData.propertyCost > 0
    ), [ mortgageFormData ]);

    const banksMortgageOffersLoader = useBanksMortgageOffersLoader();

    const { data, loading, errors } = isNeedSendRequest ?
        banksMortgageOffersLoader({
            ...mortgageFormData,
            propertyCost: Math.round(mortgageFormData.propertyCost)
        }) :
        { data: undefined, loading: false, errors: [ ] };

    const offersFull = data?.banksMortgageOffers ?? [ ];

    let credit: number | undefined = mortgageFormData.propertyCost - mortgageFormData.ownAmount;

    if (credit <= 0) credit = undefined;

    const creditFormatted = credit ? formatPrice({
        value: credit * 100,
        displayCurrency: true,
        noFractional: true,
        currency: Currency.RUB
    }) : undefined;

    return {
        mortgageFormData,
        setMortgageFormData: setFormData,
        offersFull,
        credit,
        creditFormatted,
        errors,
        loading,
        isEmpty: offersFull.length === 0 && ! loading
    };
};

export type UseBanksMortgageOffersResult = ReturnType<typeof useBanksMortgageOffers>;
